/**
 * Date helpers and naming for the athlete training calendar.
 * Day windows match the Expo app so Coach and athlete grids agree.
 */

export const EXTERNAL_TRAINING_SESSION_TYPE = 'external';

const DAYS_PER_WEEK = 7;

export function toISODate(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function dateKey(value) {
  if (!value) return null;
  return String(value).slice(0, 10);
}

/** Monday-first weekday index (0 = Monday). */
export function mondayFirstIndex(date) {
  return (date.getDay() + 6) % 7;
}

export function monthRange(month) {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  return {
    from: toISODate(new Date(year, monthIndex, 1)),
    to: toISODate(new Date(year, monthIndex + 1, 0)),
  };
}

export function dayWindow(date) {
  return {
    start: `${date}T00:00:00`,
    end: `${date}T23:59:59.999`,
  };
}

export function monthWindow(from, to) {
  return {
    start: `${from}T00:00:00`,
    end: `${to}T23:59:59.999`,
  };
}

export function defaultDateForMonth(month, todayISO) {
  const firstOfMonth = toISODate(new Date(month.getFullYear(), month.getMonth(), 1));
  return todayISO.slice(0, 7) === firstOfMonth.slice(0, 7) ? todayISO : firstOfMonth;
}

export function msUntilNextMidnight(from) {
  const nextMidnight = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1);
  return nextMidnight.getTime() - from.getTime();
}

export function buildWeekdayLabels(locale = 'en') {
  return Array.from({ length: DAYS_PER_WEEK }, (_, index) =>
    new Date(2024, 0, 1 + index).toLocaleDateString(locale, { weekday: 'narrow' })
  );
}

export function buildMonthCells({
  month,
  todayISO,
  selectedDate,
  completedByDate,
  assignedByDate,
  programByDate,
}) {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstOfMonth = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  const paddingCell = {
    date: null,
    dayNumber: null,
    isToday: false,
    isSelected: false,
    assignedCount: 0,
    completedCount: 0,
    programCount: 0,
  };

  const leadingPadding = Array.from({ length: mondayFirstIndex(firstOfMonth) }, () => paddingCell);

  const monthDays = Array.from({ length: daysInMonth }, (_, index) => {
    const date = toISODate(new Date(year, monthIndex, index + 1));
    return {
      date,
      dayNumber: index + 1,
      isToday: date === todayISO,
      isSelected: date === selectedDate,
      assignedCount: assignedByDate.get(date)?.length ?? 0,
      completedCount: completedByDate.get(date)?.length ?? 0,
      programCount: programByDate?.get(date)?.length ?? 0,
    };
  });

  const cellsSoFar = leadingPadding.length + monthDays.length;
  const remainder = cellsSoFar % DAYS_PER_WEEK;
  const trailingPadding = Array.from(
    { length: remainder === 0 ? 0 : DAYS_PER_WEEK - remainder },
    () => paddingCell
  );

  return [...leadingPadding, ...monthDays, ...trailingPadding];
}

export function resolveDayActivityName(row) {
  const session = Array.isArray(row.sessions) ? row.sessions[0] : row.sessions;
  const isExternal =
    row.activity_type != null || session?.session_type === EXTERNAL_TRAINING_SESSION_TYPE;

  if (isExternal) {
    return {
      type: 'external',
      name: row.custom_activity_name || row.activity_type || session?.name || 'External Training',
    };
  }

  return {
    type: 'session',
    name: session?.name ?? 'Training Session',
  };
}

export function mapCompletedRow(row) {
  const resolved = resolveDayActivityName(row);
  return {
    id: row.id,
    sessionId: resolved.type === 'session' ? row.session_id : undefined,
    type: resolved.type,
    name: resolved.name,
    durationMinutes: row.duration_seconds != null ? Math.round(row.duration_seconds / 60) : null,
    date: dateKey(row.ended_at),
  };
}
